import type { UseFormProps } from "react-hook-form";
import { z } from "zod";

import type { TravelRuleInfo, Vasp } from "~/v2/api/rest/cryptoWallet";
import type { BeneficiaryType } from "../types";

export const VASP_DID_PATTERN = /^did:[a-zA-Z0-9]*:.*$/;

export const isVaspDid = (value: string): boolean =>
  VASP_DID_PATTERN.test(value.trim());

// Field names mirror cryptowallet's `types.TravelRuleInfo` JSON tags 1:1, so
// `next_actions[].required_fields[].field` paths drop straight into RHF without
// a translation table. UI-only state (beneficiaryType, ownsWallet, isManualEntry)
// lives in the controller's local useState.
export const travelRuleSchema = z
  .object({
    beneficiary_is_self_hosted: z.boolean().nullable(),

    beneficiary_given_name: z.string().optional().default(""),
    beneficiary_family_name: z.string().optional().default(""),
    beneficiary_entity_name: z.string().optional().default(""),

    // Mirrors cryptowallet's VASP DID regex (cryptowallet/internal/types/travel_rule.go)
    beneficiary_vasp_id: z
      .string()
      .optional()
      .default("")
      .refine((v) => !v || isVaspDid(v), {
        message: 'VASP ID must be a valid DID (e.g. "did:ethr:0x1234...")',
      }),
    beneficiary_manual_entry: z
      .object({
        vasp_name: z.string().optional().default(""),
        vasp_website: z
          .string()
          .url("Please enter a valid URL (e.g., https://example.com)")
          .optional()
          .or(z.literal("")),
      })
      .default({ vasp_name: "", vasp_website: "" }),

    beneficiary_geographic_address_country: z.string().optional().default(""),
    beneficiary_geographic_address_building_number: z
      .string()
      .optional()
      .default(""),
    beneficiary_geographic_address_street_name: z
      .string()
      .optional()
      .default(""),
    beneficiary_geographic_address_town_name: z.string().optional().default(""),
    beneficiary_geographic_address_post_code: z.string().optional().default(""),
    beneficiary_country_of_residence: z.string().optional().default(""),

    originator_family_name: z.string().optional().default(""),
    originator_given_name: z.string().optional().default(""),
    originator_geographic_address_country: z.string().optional().default(""),
    originator_geographic_address_building_number: z
      .string()
      .optional()
      .default(""),
    originator_geographic_address_street_name: z
      .string()
      .optional()
      .default(""),
    originator_geographic_address_town_name: z.string().optional().default(""),
    originator_geographic_address_post_code: z.string().optional().default(""),
    originator_country_of_residence: z.string().optional().default(""),
    originator_date_of_birth: z.string().optional().default(""),
    originator_place_of_birth: z.string().optional().default(""),
    originator_national_identifier: z.string().optional().default(""),
    originator_national_identifier_type: z.string().optional().default(""),
    originator_entity_name: z.string().optional().default(""),
  })
  .passthrough();

export type TravelRuleFormValues = z.input<typeof travelRuleSchema> & {
  [key: string]: unknown;
};

// Zod's `.passthrough()` widens the index signature to `unknown`, but RHF's
// `useForm` expects `{ [key: string]: {} | undefined }` on `defaultValues`.
// The two index signatures are structurally incompatible, so a direct
// `useForm<TravelRuleFormValues>({ defaultValues })` fails typecheck.
// Cast the options object to this type at each `useForm` call site instead.
export type TravelRuleUseFormProps = UseFormProps<TravelRuleFormValues>;

export interface TravelRuleRefinementUIState {
  beneficiaryType: BeneficiaryType | null;
  isManualEntry: boolean;
}

export const makeTravelRuleRefinement =
  (
    ui: TravelRuleRefinementUIState,
  ): ((
    data: z.output<typeof travelRuleSchema>,
    ctx: z.RefinementCtx,
  ) => void) =>
  (data, ctx) => {
    if (ui.beneficiaryType === "individual") {
      if (!data.beneficiary_given_name?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["beneficiary_given_name"],
          message: "First name is required",
        });
      }
      if (!data.beneficiary_family_name?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["beneficiary_family_name"],
          message: "Last name is required",
        });
      }
    }

    if (ui.beneficiaryType === "business") {
      if (!data.beneficiary_entity_name?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["beneficiary_entity_name"],
          message: "Entity name is required",
        });
      }
    }

    if (data.beneficiary_is_self_hosted === false) {
      if (ui.isManualEntry) {
        if (!data.beneficiary_manual_entry?.vasp_name?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["beneficiary_manual_entry", "vasp_name"],
            message: "Exchange name is required",
          });
        }
        if (!data.beneficiary_manual_entry?.vasp_website?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["beneficiary_manual_entry", "vasp_website"],
            message: "Exchange website is required",
          });
        }
      } else if (!data.beneficiary_vasp_id) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["beneficiary_vasp_id"],
          message: "Please select or enter an exchange",
        });
      }
    }

    if (data.beneficiary_is_self_hosted === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["beneficiary_is_self_hosted"],
        message: "Please select a wallet type",
      });
    }
  };

export const TRAVEL_RULE_DEFAULTS: TravelRuleFormValues = {
  beneficiary_is_self_hosted: null,
  beneficiary_given_name: "",
  beneficiary_family_name: "",
  beneficiary_entity_name: "",
  beneficiary_vasp_id: "",
  beneficiary_manual_entry: { vasp_name: "", vasp_website: "" },
  beneficiary_geographic_address_country: "",
  beneficiary_geographic_address_building_number: "",
  beneficiary_geographic_address_street_name: "",
  beneficiary_geographic_address_town_name: "",
  beneficiary_geographic_address_post_code: "",
  beneficiary_country_of_residence: "",
  originator_family_name: "",
  originator_given_name: "",
  originator_geographic_address_country: "",
  originator_geographic_address_building_number: "",
  originator_geographic_address_street_name: "",
  originator_geographic_address_town_name: "",
  originator_geographic_address_post_code: "",
  originator_country_of_residence: "",
  originator_date_of_birth: "",
  originator_place_of_birth: "",
  originator_national_identifier: "",
  originator_national_identifier_type: "",
  originator_entity_name: "",
};

function trimmedOrUndefined(value: string | undefined): string | undefined {
  const t = value?.trim();
  return t ? t : undefined;
}

interface WhitelistCreatePayloadBase {
  travel_rule_info: TravelRuleInfo;
}

interface WhitelistCreatePayloadPrivatelyHeld
  extends WhitelistCreatePayloadBase {
  address_type: "privately_held";
}

interface WhitelistCreatePayloadExchange extends WhitelistCreatePayloadBase {
  address_type: "exchange";
  institution_name: string;
}

export type WhitelistCreatePayload =
  | WhitelistCreatePayloadPrivatelyHeld
  | WhitelistCreatePayloadExchange;

const KNOWN_SCHEMA_KEYS = new Set(Object.keys(travelRuleSchema.shape));

/**
 * Collect passthrough (non-schema) fields that the backend requested
 * dynamically via `next_actions.required_fields`. They passed through Zod
 * thanks to `.passthrough()` and must land on the wire payload.
 */
function collectPassthroughWireFields(
  data: TravelRuleFormValues,
): Record<string, string | boolean | number | undefined> {
  const extra: Record<string, string | boolean | number | undefined> = {};
  for (const [key, value] of Object.entries(data)) {
    if (KNOWN_SCHEMA_KEYS.has(key)) {
      continue;
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      extra[key] = trimmed || undefined;
    } else if (typeof value === "boolean") {
      extra[key] = value;
    } else if (typeof value === "number" && !Number.isNaN(value)) {
      extra[key] = value;
    }
  }
  return extra;
}

// Builds the create-whitelist payload. Form state is the single source of truth:
// every populated beneficiary_*/originator_* slot is shipped, and
// `trimmedOrUndefined` drops empties so the wire only carries what the user
// typed. `ownsWallet` is UI-only and never reaches the wire.
export const buildPayload = (
  data: TravelRuleFormValues,
  selectedVasp: Vasp | null,
  isManualEntry: boolean,
): WhitelistCreatePayload => {
  const isSelfHosted = data.beneficiary_is_self_hosted === true;

  const travel_rule_info: TravelRuleInfo = {
    beneficiary_vasp_id: trimmedOrUndefined(data.beneficiary_vasp_id),
    beneficiary_is_self_hosted: isSelfHosted,
    beneficiary_given_name: trimmedOrUndefined(data.beneficiary_given_name),
    beneficiary_family_name: trimmedOrUndefined(data.beneficiary_family_name),
    beneficiary_entity_name: trimmedOrUndefined(data.beneficiary_entity_name),
    beneficiary_geographic_address_country: trimmedOrUndefined(
      data.beneficiary_geographic_address_country,
    ),
    beneficiary_geographic_address_building_number: trimmedOrUndefined(
      data.beneficiary_geographic_address_building_number,
    ),
    beneficiary_geographic_address_street_name: trimmedOrUndefined(
      data.beneficiary_geographic_address_street_name,
    ),
    beneficiary_geographic_address_town_name: trimmedOrUndefined(
      data.beneficiary_geographic_address_town_name,
    ),
    beneficiary_geographic_address_post_code: trimmedOrUndefined(
      data.beneficiary_geographic_address_post_code,
    ),
    beneficiary_country_of_residence: trimmedOrUndefined(
      data.beneficiary_country_of_residence,
    ),

    originator_family_name: trimmedOrUndefined(data.originator_family_name),
    originator_given_name: trimmedOrUndefined(data.originator_given_name),
    originator_entity_name: trimmedOrUndefined(data.originator_entity_name),
    originator_geographic_address_country: trimmedOrUndefined(
      data.originator_geographic_address_country,
    ),
    originator_geographic_address_building_number: trimmedOrUndefined(
      data.originator_geographic_address_building_number,
    ),
    originator_geographic_address_street_name: trimmedOrUndefined(
      data.originator_geographic_address_street_name,
    ),
    originator_geographic_address_town_name: trimmedOrUndefined(
      data.originator_geographic_address_town_name,
    ),
    originator_geographic_address_post_code: trimmedOrUndefined(
      data.originator_geographic_address_post_code,
    ),
    originator_country_of_residence: trimmedOrUndefined(
      data.originator_country_of_residence,
    ),
    originator_date_of_birth: trimmedOrUndefined(data.originator_date_of_birth),
    originator_place_of_birth: trimmedOrUndefined(
      data.originator_place_of_birth,
    ),
    originator_national_identifier: trimmedOrUndefined(
      data.originator_national_identifier,
    ),
    originator_national_identifier_type: trimmedOrUndefined(
      data.originator_national_identifier_type,
    ),
    ...collectPassthroughWireFields(data),
  };

  const vaspName = data.beneficiary_manual_entry?.vasp_name?.trim() ?? "";
  const vaspWebsite = data.beneficiary_manual_entry?.vasp_website?.trim() ?? "";
  if (vaspName || vaspWebsite) {
    travel_rule_info.beneficiary_manual_entry = {
      vasp_name: vaspName,
      vasp_website: vaspWebsite,
    };
  }

  if (!travel_rule_info.beneficiary_vasp_id && selectedVasp && !isManualEntry) {
    travel_rule_info.beneficiary_vasp_id = selectedVasp.did ?? selectedVasp.id;
  }

  if (isSelfHosted) {
    return { address_type: "privately_held", travel_rule_info };
  }

  const institutionName = vaspName || selectedVasp?.name || "";

  return {
    address_type: "exchange",
    institution_name: institutionName,
    travel_rule_info,
  };
};
